import {
  sectionScopeApplies,
  segmentScopeApplies,
  type GeneratedDefinition,
  type GeneratedNodeAttribute,
  type NodeAttributeTarget,
  type NodeAttributeType,
} from "@keydials/api-client";
import {
  canMoveField,
  DETAILS_TYPES,
  detailsTypeOf,
  placesFor,
  type DetailsPlaceId,
  type DetailsTypeId,
  type FieldReferences,
  type SegmentNoun,
} from "@keydials/dial-workspace";
import { builderCopy as copy, fieldCount, fill, nounValues } from "./copy";

// ---------------------------------------------------------------------------
// The Details stage (KEY-560, Phase 3): the places a dial can ask for details, the fields asked in each, and what the page says
// about them. The edits are the shared field edits; this decides what the page shows, so it can be tested without a browser.
// ---------------------------------------------------------------------------

/** Where a place's words live in the copy, which calls an action point a segment. */
const PLACE_WORDS: Record<DetailsPlaceId, keyof typeof copy.details.places> = { dial: "dial", section: "section", actionPoint: "segment", item: "item" };

export interface FieldRow {
  /** Unique on the page, since a field's key is only unique within its level. */
  id: string;
  target: string;
  key: string;
  /** The name as typed, for the name box. */
  label: string;
  /** The kind of answer it takes, or null for a type the builder doesn't know. */
  typeLabel: string | null;
  canMoveUp: boolean;
  canMoveDown: boolean;
  /** On a section or action point field limited to ones that have all gone: the link that asks it of every one again. */
  askOfEvery: string | null;
}

/** Fields next to each other under the same heading, or under none. */
export interface FieldRun {
  title: string | null;
  rows: FieldRow[];
}

export interface PlaceView {
  id: DetailsPlaceId;
  target: NodeAttributeTarget;
  name: string;
  hint: string;
  /** "No fields yet", "1 field", "3 fields". */
  count: string;
  /** The place's fields in the order they're stored. */
  runs: FieldRun[];
}

export interface DetailsView {
  /** The line under the question. A dial with one colour per action point has no items, so it doesn't mention them. */
  openingLine: string;
  places: PlaceView[];
  /** Whether any place asks for anything, which turns Skip for now into Continue. */
  hasFields: boolean;
}

export const fieldRowId = (target: string, key: string): string => `${target}:${key}`;

/** What Details shows for the dial as it is now. */
export function detailsView(model: GeneratedDefinition, noun: SegmentNoun): DetailsView {
  const words = nounValues(noun);
  const fields = model.nodeAttributes ?? [];

  const places = placesFor(model).map((place): PlaceView => {
    const here = fields.filter((f) => f.target === place.target);
    const runs: FieldRun[] = [];
    for (const field of here) {
      const title = field.fieldsetTitle?.trim() || null;
      const row = fieldRow(model, field, words);
      const last = runs[runs.length - 1];
      if (last && last.title === title) last.rows.push(row);
      else runs.push({ title, rows: [row] });
    }
    const placeWords = PLACE_WORDS[place.id];
    return {
      id: place.id,
      target: place.target,
      name: fill(copy.details.places[placeWords], words),
      hint: fill(copy.details.placeHints[placeWords], words),
      count: fieldCount(here.length),
      runs,
    };
  });

  const openingLine = fill(model.kind === "Itemised" ? copy.details.openingLine : copy.details.openingLineOneColour, words);
  return { openingLine, places, hasFields: places.some((place) => place.runs.length > 0) };
}

function fieldRow(model: GeneratedDefinition, field: GeneratedNodeAttribute, words: Record<string, string>): FieldRow {
  const stale = scopeReachesNothing(model, field);
  return {
    id: fieldRowId(field.target, field.key),
    target: field.target,
    key: field.key,
    label: field.label,
    typeLabel: typeLabel(field.type),
    canMoveUp: canMoveField(model, field.target, field.key, -1),
    canMoveDown: canMoveField(model, field.target, field.key, 1),
    askOfEvery: !stale ? null : field.target === "Section" ? copy.details.askOfEverySection : fill(copy.details.askOfEverySegment, words),
  };
}

/** A field type in the builder's words: one of the kinds it offers, "Table" for a template's table, or null for any other. */
export function typeLabel(type: string): string | null {
  const id = detailsTypeOf(type);
  if (id === null) return null;
  if (id === "table") return copy.details.tableType;
  if (id === "whoswho") return copy.details.personType;
  if (id === "contacts") return copy.details.contactType;
  if (id === "responsible") return copy.details.responsibleType;
  return copy.details.types[id].label;
}

/**
 * Whether a section or action point field is limited to sections or action points none of which is still on the dial. The server
 * keeps such a scope on save, since an empty one would mean every node, and refuses to publish it. Always false for a field on
 * every node, and for whole-dial and item fields, which the page doesn't offer to widen.
 */
export function scopeReachesNothing(model: GeneratedDefinition, field: GeneratedNodeAttribute): boolean {
  const scope = field.appliesToKeys;
  if (!scope?.length) return false;
  if (field.target === "Section") return !model.sections.some((s) => sectionScopeApplies(scope, s.key));
  if (field.target === "Segment") return !model.sections.some((s) => s.segments.some((g) => segmentScopeApplies(scope, s.key, g.key)));
  return false;
}

export interface TypeCard {
  id: DetailsTypeId;
  type: NodeAttributeType;
  label: string;
  hint: string;
}

/** The type picker's cards, in the order the builder offers them. A new field is named after its card until it's renamed. */
export const TYPE_CARDS: readonly TypeCard[] = DETAILS_TYPES.map(({ id, type }) => ({ id, type, ...copy.details.types[id] }));

/** What else goes when a field is removed: one line for each kind of thing that named it. */
export function removeConsequences(removed: FieldReferences): string[] {
  const lines = copy.details.removeConsequences;
  return (["statusGates", "pairings", "resetClears"] as const).filter((kind) => removed[kind].length > 0).map((kind) => lines[kind]);
}
